import React, { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import styles from './CarouselV2.module.scss'
import Skeleton from 'components/SA/Skeleton'
import LeftArrow from 'svg/SA/chevron-left-outlined-large.svg'
import RightArrow from 'svg/SA/chevron-right-outlined-large.svg'
import classNames from 'classnames'
import useSearchProvider, {
  ConstructorAttributeObject,
} from 'components/SA/SearchProvider/hooks/useSearchProvider'

type CarouselV2Props = {
  isLoading?: boolean
  title?: string
  secondaryTitle?: string
  customStyle?: string
  polaroidWidth?: number // necessary if you want to customize how much the arrows are going to scroll horizontally the carousel. Usually is going to be the width of the polaroid created for the carousel that you can get the value by `ref?.current?.offsetWidth` (but you will have to put the value in a state to be able to get the most updated one and apply to the scroll) check components/SaveForLater
  onScrollCallback?: (param: { lastVisibleItemIndex: number; direction: 'left' | 'right' }) => void
  searchProviderAttributes?: ReadonlyArray<ConstructorAttributeObject>
}

type CarouselSkeletonProps = {
  quantity?: number
}

const CarouselSkeleton: FC<CarouselSkeletonProps> = ({ quantity = 4 }) => {
  return Array.from({ length: quantity }, (_, index) => (
    <Skeleton
      key={index.toString()}
      width='var(--carouselPolaroidWidth)'
      height='var(--carouselPolaroidHeight)'
      className={styles.carouselSkeleton}
    />
  ))
}

const CarouselV2: FC<PropsWithChildren<CarouselV2Props>> = ({
  isLoading = false,
  title,
  secondaryTitle,
  polaroidWidth = 311,
  children,
  customStyle = '',
  onScrollCallback,
  searchProviderAttributes,
}) => {
  const { getSearchProviderAttributes } = useSearchProvider()
  const carouselContentRef = useRef<HTMLDivElement>(null)
  const [carouselRefDimensions, setCarouselScrollLeft] = useState<Record<string, number>>({
    carouselScrollWidth: carouselContentRef.current?.scrollWidth || 0,
    carouselClientWidth: carouselContentRef.current?.clientWidth || 0,
    carouselScrollLeft: carouselContentRef.current?.scrollLeft || 0,
  })
  const [isAtRightEdge, setIsAtRightEdge] = useState<boolean>(false)
  const isAtLeftEdge = carouselRefDimensions.carouselScrollLeft === 0
  const isArrowsVisible = !(isAtLeftEdge && isAtRightEdge)

  const handleCarouselScroll = (direction: 'left' | 'right') => {
    if (carouselContentRef.current) {
      const itemWidth = polaroidWidth // polaroid width
      const gap = 24 // Fixed gap between items in px
      const totalItemWidth = itemWidth + gap // Total width of one item (including the gap)
      // Get the width of the carousel
      const carouselWidth = carouselContentRef.current?.offsetWidth || 0
      // Get the current scroll position
      const currentScrollLeft = carouselRefDimensions.carouselScrollLeft
      // Calculate the index of the last fully visible item
      const lastVisibleItemIndex = Math.floor((currentScrollLeft + carouselWidth) / totalItemWidth)
      // Calculate the position of the right edge of the last visible item
      const lastVisibleItemRightEdge = (lastVisibleItemIndex + 1) * totalItemWidth

      // The last item is fully visible, scroll to the next item
      const isLastItemFullVisible = lastVisibleItemRightEdge <= currentScrollLeft + carouselWidth
      const scrollRightFullAmount = (lastVisibleItemIndex + 1) * totalItemWidth - currentScrollLeft
      const scrollRightPartialAmount = lastVisibleItemIndex * totalItemWidth - currentScrollLeft
      const scrollLeftlAmount = scrollRightPartialAmount * -1 // Negative value to scroll left
      const scrollRightAmount = isLastItemFullVisible
        ? scrollRightFullAmount
        : scrollRightPartialAmount

      onScrollCallback?.({ lastVisibleItemIndex, direction })

      carouselContentRef.current.scrollBy({
        left: direction === 'right' ? scrollRightAmount : scrollLeftlAmount,
        behavior: 'smooth',
      })
    }
  }

  const handleScroll = () => {
    if (carouselContentRef.current) {
      const {
        scrollWidth: carouselScrollWidth,
        clientWidth: carouselClientWidth,
        scrollLeft: carouselScrollLeft,
      } = carouselContentRef.current
      setCarouselScrollLeft({
        carouselScrollWidth: carouselContentRef.current.scrollWidth,
        carouselClientWidth: carouselContentRef.current.clientWidth,
        carouselScrollLeft: carouselContentRef.current.scrollLeft,
      })

      const isAtScrollEnd = carouselScrollWidth - carouselClientWidth - carouselScrollLeft < 2 // on IOS sometimes there is 1px of difference from the scrolls
      setIsAtRightEdge(isAtScrollEnd)
    }
  }

  useEffect(() => {
    if (!isLoading) {
      // Timeout ensures the DOM has finished rendering children
      setTimeout(() => {
        handleScroll()
      }, 0)
    }
  }, [isLoading])

  useEffect(() => {
    const container = carouselContentRef.current
    container?.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleScroll)

    return () => {
      container?.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  return (
    <div className={classNames(styles.carouselWrapper, { [customStyle]: customStyle !== '' })}>
      <div className={styles.headerContainer}>
        <p className={styles.titleBox}>
          {title && <span>{title}</span>}
          {secondaryTitle && <span>{secondaryTitle}</span>}
        </p>

        {isArrowsVisible && (
          <div className={styles.arrowsBox}>
            <LeftArrow
              className={classNames({
                [styles.disableArrow]: isAtLeftEdge,
              })}
              onClick={() => handleCarouselScroll('left')}
            />
            <RightArrow
              className={classNames({
                [styles.disableArrow]: isAtRightEdge,
              })}
              onClick={() => handleCarouselScroll('right')}
            />
          </div>
        )}
      </div>

      <div
        className={styles.carouselContent}
        ref={carouselContentRef}
        data-style='carousel-content'
        {...(!!searchProviderAttributes
          ? getSearchProviderAttributes(searchProviderAttributes)
          : {})}
      >
        {isLoading ? (
          <CarouselSkeleton quantity={carouselRefDimensions.carouselClientWidth % polaroidWidth} />
        ) : (
          children
        )}
      </div>
    </div>
  )
}

export default CarouselV2
